import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import router from '../../versa/router.js';
import formset from '../../versa/formset.js';
import pdf from '../../versa/pdf.js';
import { flatten } from '../../versa/record.js';
import { notify, ask } from '../../versa/msgbox.js';
import { splitIdsByCompany } from '../../versa/misc.js';
import view from '../views/invoices.js';
import form from '../forms/invoices.js';
import create_lots_form from '../forms/invoices-create-lots.js';
import inutilizar_form from '../forms/invoices-inutilizar.js';
import iqvia_report_form from '../forms/invoices-iqvia-report.js';
import * as types from '../types.js';

const invoices = function()
{

	let invoices_crud = crud.create({
		async loadBaseData()
		{
			api.call(api.list_companies, this.queryAll()).then((data) => db.set('companies', data).catch(error => notify(error)));
			api.call(api.list_cfops, this.queryAll()).then((data) => db.set('cfops', data).catch(error => notify(error)));
		},
		async queryData(query)
		{
			return await api.call(api.list_invoices, query);
		},
		async loadRecord(id)
		{
			let _record = await api.call(api.select_invoice, ...JSON.parse(id));
			return flatten(_record, 'protocols');
		},
		async saveRecord(data, old_data)
		{
			let _record = {
				io_type: data.io_type,
				io_id: Number(data.io_id),
				cfop: data.cfop,
				insurance: Number(data.insurance),
				expenses: Number(data.expenses),
				info: data.info?.trim(),
				note: data.note?.trim(),
				ref_key: data.ref_key,
			};
			if (old_data)
				return await api.call(api.update_invoice, data.company_id, data.id, _record);
			else
				return await api.call(api.insert_invoice, data.company_id, _record);
		},
		updateXML()
		{
			if (!this.ensureSelection('Atualizar XML'))
				return;
			const showResult = (resp) =>
			{
				notify(`Notas atualizadas: ${resp?.count || 0}`, 'Resultado');
				this.loadPage();
			}
			const sendRequest = async () =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				const resp = await api.call(api.update_invoices_xml, { 'ids': ids });
				if (resp != null)
					showResult(resp);
			}
			ask(`Atualizar XML de ${this.getCount()} notas?`, 'Confirmação', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		createLots()
		{
			if (!this.ensureSelection('Gerar lotes de notas'))
				return;
			const showResult = (resp) =>
			{
				notify(`Lotes criados: ${resp?.count || 0}`, 'Resultado');
				this.loadPage();	// Required on corrections and cancelations
				fs.destroy();
			}
			const sendRequest = async (data) =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				let lots = splitIdsByCompany(ids);
				const resp = await api.call(api.create_invoice_lots, Object.assign(data, { 'lots': lots }));
				if (resp != null)
					showResult(resp);
			}
			let fs = formset.create(create_lots_form, sendRequest);
			fs.init = (() =>
			{
				document.querySelector(`${fs.form.selector} #count`).innerHTML = this.getCount();
				Object.getPrototypeOf(fs).init.call(fs);
			})();
		},
		createArchive()
		{
			if (!this.ensureSelection('Baixar XML e DANFE'))
				return;
			const showResult = (resp) =>
			{
				this.clearGridSelection();
				if (resp)
					notify(`<a href="data:application/x-zip-compressed;base64,${resp.zip}" download="invoices.zip">Clique aqui para obter o arquivo</a>`, 'Download');
			}
			const sendRequest = async (data) =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				const resp = await api.call(api.create_archive, { 'ids': ids });
				if (resp != null)
					showResult(resp);
			}
			ask(`Baixar XML e DANFE de ${this.getCount()} notas?`, 'Confirmação', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		inutilizarNF()
		{
			const showResult = (data) =>
			{
				if (data?.ok)
					notify(`Inutilização homologada. Protocolo: ${data.protocolo || ''}`, 'Resultado');
				else if (data)
					notify(`${data?.mensagem || 'Erro'} (${data?.codigo || ''})`, 'Erro');
				fs.destroy();
				this.loadPage();
			};
			const sendRequest = async (data) =>
			{
				const body = {
					company_id: data.company_id,
					serie: Number(data.serie),
					nNFIni: Number(data.nNFIni),
					nNFFin: Number(data.nNFFin),
					xJust: data.xJust?.trim(),
				};
				const resp = await api.call(api.inutilizar_invoices, body);
				if (resp) showResult(resp);
				else { fs.destroy(); this.loadPage(); }
			};
			let fs = formset.create(inutilizar_form, sendRequest);
			fs.init();
		},
		printIqvia()
		{
			const sendRequest = async (data) =>
			{
				fs.destroy();
				try {
					const payload = {
						company_id: data.company_id,
						date_from: data.date_from
					};
					if (data.date_to && String(data.date_to).trim())
						payload.date_to = data.date_to;
					const resp = await api.call(api.print_iqvia, payload);
					if (resp?.pdf) pdf(resp.pdf);
					else notify('Nenhum dado para o relatório.', 'Aviso');
				} catch (e) {
					notify(e?.message || String(e), 'Erro');
				}
			};
			let fs = formset.create(iqvia_report_form, sendRequest);
			fs.init();
		},
	});
	
	// Index = position in the backend's SELECT
	let fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'id', label: 'Nota', types: [types.number(0, 2)], placeholder: '0', title: 'Número da nota fiscal' },
		{ index: 3, field: 'io_id', label: 'E/S', types: [types.number(0, 2)], placeholder: '0', title: 'Número da entrada/saída vinculada' },
		{ index: 4, field: 'io_type', label: 'Origem', types: [types.opt_io_type()], placeholder: '', title: 'Origem da entrada/saída vinculada' },
		{ index: 5, field: 'cfop', label: 'CFOP', types: [types.opt_cfop(), types.datalist()], placeholder: '', title: 'Código Fiscal de Operações e Prestações' },
		{ index: 6, field: 'insurance', label: 'Seguro', types: [types.number(2)], placeholder: '0,00', title: 'Valor do seguro' },
		{ index: 7, field: 'expenses', label: 'Despesas', types: [types.number(2)], placeholder: '0,00', title: 'Valor das despesas acessórias' },
		{ index: 8, field: 'info', label: 'Informações', types: [], placeholder: '', title: 'Informações complementares' },
		{ index: 9, field: 'key', label: 'Chave', types: [types.nfe_key()], placeholder: '0000 0000 0000 0000 0000 0000 0000 0000 0000 0000', title: 'Chave da nota fiscal' },
		{ index: 10, field: 'ref_key', label: 'Referenciada', types: [types.nfe_key()], placeholder: '0000 0000 0000 0000 0000 0000 0000 0000 0000 0000', title: 'Chave da nota referenciada' },
		{ index: 12, field: 'note', label: 'Anotação', types: [], placeholder: '', title: 'Anotações de correções ou cancelamento' },
		{ index: 13, field: 'last_code', label: 'Código', types: [types.number(0, 2)], placeholder: '0', title: 'Código de retorno da última operação' },
		{ index: 14, field: 'last_text', label: 'Mensagem', types: [], placeholder: '', title: 'Mensagem de retorno da última operação' },
		{ index: 15, field: 'last_datetime', label: 'Data/Hora', types: [types.datetime()], placeholder: 'dd/mm/aaaa hh:mm:ss', title: 'Data e hora da última operação' },
		{ index: 16, field: 'inutilizacao_protocolo', label: 'Inutilizada', types: [], placeholder: '', title: 'Protocolo da inutilização na SEFAZ (vazio se não inutilizada)' },
	];

	let operations = [
		{ hash: '#update-xml',		label: 'Atualizar&nbsp;XML'						},
		{ hash: '#create-lots',		label: 'Gerar&nbsp;lotes&nbsp;de&nbsp;notas'	},
		{ hash: '#create-archive',	label: 'Baixar&nbsp;XML&nbsp;e&nbsp;DANFE'		},
		{ hash: '#inutilizar-nf',	label: 'Inutilizar&nbsp;NF'						},
	];

	let reports = [
		{ hash: '#print-iqvia',		label: 'Relatório&nbsp;IQVIA'					},
	];

	router.set('#update-xml', invoices_crud.updateXML.bind(invoices_crud));
	router.set('#create-lots', invoices_crud.createLots.bind(invoices_crud));
	router.set('#create-archive', invoices_crud.createArchive.bind(invoices_crud));
	router.set('#inutilizar-nf', invoices_crud.inutilizarNF.bind(invoices_crud));
	router.set('#print-iqvia', invoices_crud.printIqvia.bind(invoices_crud));

	invoices_crud.run(
		'invoices',
		'Notas&nbsp;Fiscais',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		operations,
		reports
	);

};

export default invoices;

